import { ReactNode, useEffect, useMemo, useState } from "react";
import clsx from "clsx";
import useObservable from "@core/hooks/use-observable.hook";
import UserGuideService from "@app/services/http/user.guide.service";
import { UserGuide } from "@app/types";
import { Environment } from "@environments/environment";
import { useT } from "@app/contexts/preferences.context";
import {
  IcCircleHelp,
  IcDownload,
  IcExternalLink,
  IcFileText,
  IcFolder,
  IcGamepad,
  IcLink,
  IcRocket,
  IcSearch,
  IcUsb,
  IcWallet,
} from "@app/components/icons";
import { LoadingBlock, btn, btnGhost, btnPrimary, btnSm } from "@app/components/ui";
import { MAX_LEN } from "@app/constants";

type Topic = [string, string, typeof IcRocket];

/** Chủ đề có sẵn theo bản thiết kế, khớp trường topic của user_guide */
const BUILT_IN: Topic[] = [
  ["start", "Bắt đầu", IcRocket],
  ["play", "PCRender Play", IcGamepad],
  ["tunnel", "PCRender Tunnel", IcLink],
  ["device", "Máy thuê & thiết bị", IcUsb],
  ["pay", "Gia hạn & thanh toán", IcWallet],
  ["game", "Game", IcDownload],
];
const BUILT_IN_KEYS = BUILT_IN.map(([key]) => key);
const OTHER: Topic = ["other", "Khác", IcFolder];
const ALL = "all";
/** chủ đề admin tự nhập: key có tiền tố để không trùng "all" / "other" / chủ đề có sẵn */
const CUSTOM = "c:";

/** Chủ đề có sẵn giữ mã; chủ đề admin tự nhập thành "c:<chữ>"; chưa có chủ đề (dữ liệu cũ) xếp vào "Khác" */
const topicOf = (g: UserGuide) => {
  const topic = (g.topic || "").trim();

  if (!topic) return "other";

  return BUILT_IN_KEYS.includes(topic) ? topic : CUSTOM + topic;
};

/** Bỏ dấu tiếng Việt để tìm kiếm */
const fold = (s: string) =>
  s
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/đ/g, "d")
    .replace(/Đ/g, "D")
    .toLowerCase();

/** Tô đậm phần khớp từ khoá */
const highlight = (text: string, q: string): ReactNode => {
  if (!q) return text;

  const i = fold(text).indexOf(q);

  if (i < 0) return text;

  return (
    <>
      {text.slice(0, i)}
      <mark className="rounded bg-brand-soft px-0.5 text-brand">{text.slice(i, i + q.length)}</mark>
      {text.slice(i + q.length)}
    </>
  );
};

/** Trang Hướng dẫn sử dụng: danh sách từ GET /user-guide (admin quản lý tiêu đề, chủ đề, link), mở ở tab mới */
function UserGuideManagement() {
  const t = useT();
  const [guides, setGuides] = useState<UserGuide[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [q, setQ] = useState("");
  const [topic, setTopic] = useState(ALL);

  const { subscribeUntilDestroy } = useObservable();

  useEffect(() => {
    subscribeUntilDestroy(UserGuideService.getAllUserGuides(), (res) => {
      setGuides(res?.data || []);
      setLoaded(true);
    });
  }, []);

  const query = fold(q.trim());

  // chủ đề có sẵn, rồi các chủ đề admin tự nhập (theo thứ tự chữ cái), "Khác" luôn ở cuối
  const topics = useMemo<Topic[]>(() => {
    const customs = Array.from(new Set(guides.map(topicOf).filter((key) => key.startsWith(CUSTOM)))).sort((a, b) =>
      a.localeCompare(b, "vi")
    );

    return [...BUILT_IN, ...customs.map((key): Topic => [key, key.slice(CUSTOM.length), IcFolder]), OTHER];
  }, [guides]);
  const topicInfo = (key: string) => topics.find(([k]) => k === key) || OTHER;

  const counts = useMemo(() => {
    const map: Record<string, number> = { [ALL]: guides.length };

    guides.forEach((g) => (map[topicOf(g)] = (map[topicOf(g)] || 0) + 1));

    return map;
  }, [guides]);

  const results = guides.filter((g) => (topic === ALL || topicOf(g) === topic) && (!query || fold(g.title).includes(query)));
  const featured = guides.filter((g) => g.is_featured);

  const row = (g: UserGuide, showTopic: boolean) => (
    <a
      key={g._id}
      href={g.link}
      target="_blank"
      rel="noopener noreferrer"
      className="group flex items-center gap-3 border-t border-line px-4 py-3 text-fg first:border-t-0 hover:bg-surface-2"
    >
      <IcFileText className="h-[18px] w-[18px] flex-none text-faint" />
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <b className="font-medium">{highlight(g.title, query)}</b>
        {showTopic && <small className="text-xs text-muted">{t(topicInfo(topicOf(g))[1])}</small>}
      </span>
      <IcExternalLink className="h-4 w-4 flex-none text-faint group-hover:text-brand" />
      <span className="sr-only">({t("mở trong tab mới")})</span>
    </a>
  );

  const groupHead = (title: ReactNode, count: number, Icon?: typeof IcRocket) => (
    <div className="flex items-center gap-2.5 border-b border-line px-4 py-3">
      {Icon && (
        <span className="grid h-8 w-8 flex-none place-items-center rounded-[9px] bg-brand-soft text-brand">
          <Icon className="h-4 w-4" />
        </span>
      )}
      <h2 className="flex-1 text-[15px] font-bold">{title}</h2>
      <em className="text-xs not-italic tabular-nums text-faint">{count}</em>
    </div>
  );

  let body: ReactNode;

  if (!loaded) {
    body = <LoadingBlock className="rounded-[14px] border border-line bg-surface" />;
  } else if (!results.length) {
    body = (
      <div className="flex flex-col items-center gap-1.5 rounded-[14px] border border-line bg-surface px-4 py-12 text-center">
        <div className="mb-2 grid h-14 w-14 place-items-center rounded-2xl bg-brand-soft text-brand">
          <IcSearch className="h-[26px] w-[26px]" />
        </div>
        <h3 className="text-base font-bold">{guides.length ? t("Không tìm thấy hướng dẫn nào") : t("Chưa có hướng dẫn nào")}</h3>
        {!!guides.length && (
          <>
            <p className="m-0 text-muted">{t("Thử từ khoá ngắn hơn, ví dụ \"token\", \"tunnel\" hoặc \"gia hạn\".")}</p>
            <button
              type="button"
              className={clsx(btn, btnGhost, btnSm, "mt-2")}
              onClick={() => {
                setQ("");
                setTopic(ALL);
              }}
            >
              {t("Xoá tìm kiếm")}
            </button>
          </>
        )}
      </div>
    );
  } else if (!query && topic === ALL) {
    body = (
      <>
        {!!featured.length && (
          <section aria-label={t("Bắt đầu nhanh")} className="grid grid-cols-3 gap-3 max-[760px]:grid-cols-1">
            {featured.map((g) => {
              const [, label, Icon] = topicInfo(topicOf(g));

              return (
                <a
                  key={g._id}
                  href={g.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex flex-col gap-2 rounded-[14px] border border-line bg-surface p-4 text-fg transition-shadow hover:border-brand-line hover:shadow-card max-[760px]:flex-row max-[760px]:flex-wrap max-[760px]:items-center"
                >
                  <span className="grid h-9 w-9 flex-none place-items-center rounded-[10px] bg-brand-soft text-brand">
                    <Icon className="h-[18px] w-[18px]" />
                  </span>
                  <b className="font-semibold leading-snug max-[760px]:min-w-0 max-[760px]:flex-1">{g.title}</b>
                  <small className="mt-auto flex items-center justify-between text-xs text-muted max-[760px]:w-full">
                    {t(label)}
                    <IcExternalLink className="h-4 w-4 text-faint group-hover:text-brand" />
                  </small>
                </a>
              );
            })}
          </section>
        )}
        <div className="grid grid-cols-2 items-start gap-3.5 max-[1100px]:grid-cols-1">
          {topics.filter(([key]) => counts[key]).map(([key, label, Icon]) => (
            <section key={key} className="overflow-hidden rounded-[14px] border border-line bg-surface">
              {groupHead(t(label), counts[key], Icon)}
              {guides.filter((g) => topicOf(g) === key).map((g) => row(g, false))}
            </section>
          ))}
        </div>
      </>
    );
  } else {
    body = (
      <section className="overflow-hidden rounded-[14px] border border-line bg-surface">
        {groupHead(query ? `${t("Kết quả cho")} “${q.trim()}”` : t(topicInfo(topic)[1]), results.length)}
        {results.map((g) => row(g, !!query))}
      </section>
    );
  }

  return (
    <div className="flex flex-col gap-5 px-6 pb-12 pt-6 max-[1100px]:pb-24 max-[900px]:px-4">
      <div>
        <h1 className="text-[26px] font-bold tracking-[-.015em] max-[760px]:text-[22px]">{t("Hướng dẫn sử dụng")}</h1>
        <p className="m-0 mt-1 max-w-[62ch] text-muted">
          {t("Tìm nhanh hướng dẫn theo chủ đề. Bài hướng dẫn mở ở trang riêng trong tab mới.")}
        </p>
      </div>

      <div className="flex flex-col gap-3">
        <label className="flex h-12 items-center gap-2 rounded-xl border border-line bg-surface px-3.5 text-muted focus-within:border-brand focus-within:shadow-[0_0_0_3px_var(--brand-soft)]">
          <IcSearch className="h-5 w-5 flex-none" />
          <input
            type="search"
            maxLength={MAX_LEN.SEARCH}
            value={q}
            autoComplete="off"
            onChange={(e) => setQ(e.target.value)}
            placeholder={t("Bạn cần giúp gì? Ví dụ: token, micro, tunnel, gia hạn…")}
            className="min-w-0 flex-1 border-0 bg-transparent text-[15px] text-fg outline-none"
          />
        </label>
        <div className="flex flex-wrap gap-2" role="group" aria-label={t("Chủ đề hướng dẫn")}>
          {[[ALL, "Tất cả"] as [string, string], ...topics.filter(([key]) => counts[key]).map(([key, label]) => [key, label] as [string, string])].map(
            ([key, label]) => (
              <button
                key={key}
                type="button"
                aria-pressed={topic === key}
                onClick={() => setTopic(key)}
                className={clsx(
                  "inline-flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-[13px] font-medium",
                  topic === key ? "border-fg bg-fg text-surface" : "border-line bg-surface text-muted hover:text-fg"
                )}
              >
                {t(label)}
                <em className={clsx("text-xs not-italic tabular-nums", topic === key ? "opacity-70" : "text-faint")}>
                  {counts[key] || 0}
                </em>
              </button>
            )
          )}
        </div>
      </div>

      {body}

      <div className="flex flex-wrap items-center gap-3 rounded-[14px] border border-dashed border-line px-4 py-3.5">
        <IcCircleHelp className="h-5 w-5 flex-none text-brand" />
        <div className="min-w-0 flex-1">
          <b className="block">{t("Chưa tìm thấy câu trả lời?")}</b>
          <span className="text-[13px] text-muted">{t("Đội hỗ trợ trực trong thời gian 8h30-12h00 và 13h30-21h00 hàng ngày qua chat.")}</span>
        </div>
        <a className={clsx(btn, btnPrimary, btnSm)} href={Environment.FACEBOOK_PAGE} target="_blank" rel="noopener noreferrer">
          {t("Liên hệ hỗ trợ")}
        </a>
      </div>
    </div>
  );
}

export default UserGuideManagement;
